// logic/evaluateBaby.js
// Pure function to compute Baby SKU from answers + questions (JSON you provided)
export default function evaluateBaby({ answers = {}, questions = [] }) {
  // Flatten questions array into map by sym_id
  const byId = new Map();
  const all = Array.isArray(questions) ? questions : (questions?.anamnese || []);
  const groups = Array.isArray(all) ? all : [];
  for (const g of groups) {
    for (const q of (g.questions || [])) {
      byId.set(Number(q.sym_id), q);
    }
  }

  const scores = { Comfort: 0, Immune: 0, Brain: 0, Regenera: 0 };
  const majors = { Comfort: 0, Immune: 0, Brain: 0, Regenera: 0 }; // count of +2/+3 events per axis
  const usedByAxis = { Comfort: new Set(), Immune: new Set(), Brain: new Set(), Regenera: new Set() };
  const reasons = []; // {axis, pts, label}

  const hasAns = id => answers?.[id] != null && (answers[id].answer != null || typeof answers[id] !== 'object');
  const getRaw = id => {
    const a = answers?.[id];
    if (!a) return undefined;
    if (typeof a === 'object' && a.answer !== undefined) return a.answer;
    return a;
  };
  const getRadioYes = id => {
    const v = Number(getRaw(id));
    if (Number.isNaN(v)) return undefined;
    return v === 1; // backend envia 1/0
  };
  const getRange = id => {
    const v = Number(getRaw(id));
    if (Number.isNaN(v)) return undefined;
    return v; // 0..5
  };
  const getButtonLabel = id => {
    const a = answers?.[id];
    let v = (typeof a === 'object' && a?.answer !== undefined) ? a.answer : a;
    if (v == null) return undefined;
    // Se vier ans_id numérico, mapeia para o label
    if (typeof v === 'number') {
      const q = byId.get(Number(id));
      const opt = (q?.answers || []).find(o => Number(o.ans_id) === Number(v));
      return opt?.label ?? String(v);
    }
    return String(v);
  };

  const add = (axis, pts, id, label) => {
    scores[axis] += pts;
    usedByAxis[axis].add(id);
    reasons.push({ axis, pts, label, id });
    if (pts >= 2) majors[axis] += 1;
  };

  // ---- Etapa 2 — Rotina alimentar & digestão ----
  if (getRadioYes(307)) add('Comfort', 1, 307, 'Leite/fórmula ≥ 3x/dia');
  if (getRadioYes(308)) add('Comfort', 1, 308, 'Mamadeira para adormecer/madrugada');

  const r309 = getRange(309);
  if (r309 !== undefined) {
    if (r309 <= 2) add('Comfort', 1, 309, 'Poucas refeições sólidas por dia');
    if (r309 <= 1) add('Regenera', 1, 309, 'Refeições sólidas muito escassas (imaturidade)');
  }

  if (getRadioYes(310)) add('Comfort', 1, 310, 'Adoça bebidas');

  const r311 = getRange(311);
  if (r311 !== undefined && r311 <= 2) add('Brain', 1, 311, 'Aceita pouco novos alimentos');

  const r312 = getRange(312);
  if (r312 !== undefined && r312 <= 2) add('Brain', 1, 312, 'Pouco peixe/ômega-3');

  const r473 = getRange(473);
  if (r473 !== undefined) {
    if (r473 >= 3) add('Comfort', 1, 473, 'Industrializados adoçados frequentes');
    if (r473 >= 4) add('Regenera', 1, 473, 'Exposição inflamatória alta (doces)');
  }

  // ---- Etapa 3 — Conforto digestivo ----
  const r474 = getRange(474);
  if (r474 !== undefined && r474 >= 3) add('Comfort', 1, 474, 'Distensão/gases após comer');

  const r475 = getRange(475);
  if (r475 !== undefined) {
    if (r475 >= 3) add('Comfort', 1, 475, 'Alternância constipação/diarreia');
    if (r475 >= 4) add('Regenera', 1, 475, 'Alternância intensa (fragilidade intestinal)');
  }

  const r476 = getRange(476);
  if (r476 !== undefined && r476 >= 3) add('Comfort', 1, 476, 'Regurgitação/refluxo');

  if (hasAns(477) && getRadioYes(477) === false) add('Comfort', 1, 477, 'Não evacua diariamente');

  if (getRadioYes(478)) add('Comfort', 1, 478, '≥3 evacuações/dia em alguma semana');

  if (getRadioYes(479)) add('Regenera', 2, 479, 'Dor/sangramento ao evacuar (últimas 2 semanas)');

  if (getRadioYes(480)) add('Regenera', 2, 480, 'Uso atual de laxante/supositório');

  if (getRadioYes(481)) add('Regenera', 2, 481, 'Fezes muito claras/pretas com frequência');

  const fezes = getButtonLabel(482);
  if (fezes) {
    if (/Durinhas|duras|bolinhas/i.test(fezes)) add('Comfort', 1, 482, 'Fezes duras/bolinhas');
    if (/Muito líquidas|muito líquidas|liquidas/i.test(fezes)) {
      add('Comfort', 1, 482, 'Fezes muito líquidas');
      add('Regenera', 1, 482, 'Fezes muito líquidas (fragilidade)');
    }
  }

  const r483 = getRange(483);
  if (r483 !== undefined && r483 <= 2) add('Comfort', 1, 483, 'Pouca oferta de água');

  // ---- Etapa 4 — Pele, alergias e primeiras exposições ----
  const r484 = getRange(484);
  if (r484 !== undefined && r484 >= 3) add('Immune', 1, 484, 'Pele ressecada/descamando');

  const r485 = getRange(485);
  if (r485 !== undefined && r485 >= 3) add('Immune', 1, 485, 'Vermelhidão/urticária pós-exposição');

  if (getRadioYes(486)) { add('Immune', 2, 486, 'Dermatite atópica/eczema/psoríase'); add('Regenera', 1, 486, 'Dermatite atópica/eczema/psoríase'); }

  const r487 = getRange(487);
  if (r487 !== undefined && r487 >= 3) add('Immune', 1, 487, 'Coceira que atrapalha');

  if (getRadioYes(488)) add('Immune', 2, 488, 'Uso frequente de pomada com corticoide');

  if (getRadioYes(489)) add('Immune', 1, 489, 'Histórico familiar atopia/asma/rinite');

  // 490 tipo de parto (Cesárea=0, Normal=1)
  const parto = Number(getRaw(490));
  if (!Number.isNaN(parto)) {
    if (parto === 0) add('Immune', 1, 490, 'Parto cesárea');
  }

  if (hasAns(491) && getRadioYes(491) === false) add('Immune', 1, 491, 'Sem aleitamento exclusivo até 6 meses');

  // ---- Etapa 5 — Imunidade & vitalidade ----
  if (getRadioYes(492)) add('Immune', 2, 492, '≥2 gripes/resfriados (3 meses)');
  if (getRadioYes(493)) add('Regenera', 3, 493, 'Antibiótico no último mês');
  const r494 = getRange(494);
  if (r494 !== undefined && r494 >= 3) add('Immune', 2, 494, 'Infecções respiratórias repetidas');
  const r495 = getRange(495);
  if (r495 !== undefined && r495 >= 3) add('Immune', 1, 495, 'Cansaço após pequenas brincadeiras');

  // ---- Etapa 6 — Rotina diária & telas ----
  const r496 = getRange(496);
  if (r496 !== undefined && r496 >= 4) add('Brain', 1, 496, 'Tempo de telas alto');
  const r497 = getRange(497);
  if (r497 !== undefined && r497 >= 4) add('Brain', 1, 497, 'Telas durante refeições');
  const r498 = getRange(498);
  if (r498 !== undefined && r498 <= 2) add('Brain', 1, 498, 'Pouco ar livre');
  if (hasAns(499) && getRadioYes(499) === false) add('Immune', 1, 499, 'Sem contato diário com crianças');
  if (getRadioYes(500)) add('Immune', 2, 500, 'Fumantes no ambiente');

  // ---- Etapa 7 — Sono & crescimento ----
  if (hasAns(501) && getRadioYes(501) === false) add('Immune', 1, 501, 'Adormece tarde (>20h30)');
  const r502 = getRange(502);
  if (r502 !== undefined && r502 >= 3) { add('Immune', 1, 502, 'Acorda à noite com frequência'); add('Brain', 1, 502, 'Sono fragmentado'); }
  if (getRadioYes(503)) add('Immune', 2, 503, 'Ronco/respiração pela boca');
  const r504 = getRange(504);
  if (r504 !== undefined && r504 <= 2) add('Immune', 1, 504, 'Não acorda descansado');
  if (getRadioYes(505)) { add('Comfort', 1, 505, 'Peso/altura abaixo do esperado'); add('Regenera', 1, 505, 'Peso/altura abaixo do esperado'); }
  if (getRadioYes(506)) add('Comfort', 1, 506, 'Acima do peso para a idade');

  // ---- Etapa 8 — Saúde bucal & açúcar ----
  // 507 não pontua
  if (getRadioYes(508)) add('Comfort', 1, 508, 'Bebida adoçada após escovar à noite');
  if (hasAns(509) && getRadioYes(509) === false) add('Immune', 1, 509, 'Escovação < 2x/dia');
  if (getRadioYes(510)) add('Immune', 2, 510, 'Cárie/mancha escura já observada');
  const r511 = getRange(511);
  if (r511 !== undefined) {
    if (r511 >= 3) add('Comfort', 1, 511, 'Doces entre refeições');
    if (r511 >= 4) add('Regenera', 1, 511, 'Doces frequentes (inflamatório)');
  }

  // ---- Etapa 9 — Desenvolvimento & ambiente ----
  if (getRadioYes(512)) add('Immune', 2, 512, 'Ambiente úmido/mofo/pó');
  const r513 = getRange(513);
  if (r513 !== undefined && r513 <= 2) add('Brain', 1, 513, 'Pouco apontar/gesto para pedir');
  const r514 = getRange(514);
  if (r514 !== undefined && r514 <= 2) add('Brain', 2, 514, 'Poucas palavras/frases curtas');
  const r515 = getRange(515);
  if (r515 !== undefined && r515 <= 2) add('Brain', 2, 515, 'Atraso em marcos motores');
  const r516 = getRange(516);
  if (r516 !== undefined && r516 <= 2) add('Brain', 1, 516, 'Baixa energia/curiosidade');

  // ---- PRIORIDADE REGENERA (pré-escolha dos pais) ----
  const regeneraSignalsMajor = [];
  if (getRadioYes(493)) regeneraSignalsMajor.push(493);
  if (getRadioYes(479)) regeneraSignalsMajor.push(479);
  if (getRadioYes(480)) regeneraSignalsMajor.push(480);
  if (getRadioYes(481)) regeneraSignalsMajor.push(481);
  if ((getButtonLabel(482) || "").match(/Muito líquidas/i)) regeneraSignalsMajor.push(482);
  if ((getRange(475) ?? -1) >= 4) regeneraSignalsMajor.push(475);

  const regeneraPre = scores.Regenera >= 6 || (scores.Regenera >= 4 && regeneraSignalsMajor.length >= 1);

  // ---- Pré-recomendação (sem considerar pais) ----
  const nonReg = ['Comfort', 'Immune', 'Brain'];
  const leaderNonReg = nonReg.reduce((best, k) => {
    if (!best) return k;
    if (scores[k] > scores[best]) return k;
    if (scores[k] < scores[best]) return best;
    // empate: usa critérios
    if (majors[k] > majors[best]) return k;
    if (majors[k] < majors[best]) return best;
    if (usedByAxis[k].size > usedByAxis[best].size) return k;
    if (usedByAxis[k].size < usedByAxis[best].size) return best;
    // ordem clínica padrão
    const order = ['Comfort', 'Immune', 'Brain'];
    return order.indexOf(k) < order.indexOf(best) ? k : best;
  }, null);

  let preliminarySKU = regeneraPre ? 'Regenera' : leaderNonReg;

  // ---- Etapa 10 — Prioridade dos pais ----
  const choiceLabel = getButtonLabel(517);
  const choiceMap = {
    'Conforto digestivo': 'Comfort',
    'Imunidade': 'Immune',
    'Desenvolvimento': 'Brain',
    'Regeneração': 'Regenera'
  };
  let parentAxis = undefined;
  if (choiceLabel) {
    for (const [key, axis] of Object.entries(choiceMap)) {
      if (choiceLabel.toLowerCase().includes(key.toLowerCase())) {
        parentAxis = axis;
        break;
      }
    }
  }
  const boosted = { ...scores };
  if (parentAxis) boosted[parentAxis] += 3;

  // ordena com boost (tie mantém preliminar; se tiver Regenera empatado com sinal maior, prioriza)
  let finalSKU = Object.keys(boosted).reduce((best, k) => {
    if (!best) return k;
    if (boosted[k] > boosted[best]) return k;
    if (boosted[k] < boosted[best]) return best;
    return best;
  }, null);

  const topValue = Math.max(...Object.values(boosted));
  const tied = Object.entries(boosted).filter(([k,v]) => v === topValue).map(([k])=>k);
  if (tied.length > 1) {
    if (tied.includes(preliminarySKU)) finalSKU = preliminarySKU;
    if (tied.includes('Regenera') && regeneraSignalsMajor.length >= 1) finalSKU = 'Regenera';
  }

  const topReasons = reasons
    .slice()
    .sort((a,b) => b.pts - a.pts || b.id - a.id)
    .slice(0, 5)
    .map(r => `${r.label} (+${r.pts} ${r.axis})`);

  return {
    sku: finalSKU,
    scores,                  // { Comfort, Immune, Brain, Regenera } (pontos relativos)
    regenera_flags: { G1: 0, G2: 0, G3: 0, G4: 0 }, // Baby não usa G-flags agora
    preliminarySKU,
    boostedScores: boosted,
    reasons: topReasons,     // strings "+axis"
    reasonsRich: reasons,      // [{ axis, pts, label, id }]
    alerts: []               // por ora, nenhum alerta específico no Baby
  };
}